import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BadgeIcon,
  CheckIcon,
  DrawingsIcon,
  CaptureIcon,
  LoopIcon,
  RfiIcon,
  ShieldIcon,
  TimeIcon,
} from './icons';
import './styles.scss';

const checkRows = [
  '90 days free, no credit card, no commitment',
  'On SafetyLoop? Try it free for 6 months',
  'Unlimited subcontractor accounts, free on every plan',
  'Offline-ready — drawings cached on device',
];

const recordEvents = [
  {
    tone: 'blue',
    icon: RfiIcon,
    who: 'PM',
    title: 'Logged as RFI #12 · routed to structural',
    sub: "The PM's answer — response due Thursday",
  },
  {
    tone: 'amber',
    icon: TimeIcon,
    who: 'Super',
    title: "Schedule flag on Friday's L3 pour",
    sub: "The super's answer — both crews notified",
  },
  {
    tone: 'green',
    icon: ShieldIcon,
    who: 'Safety',
    via: 'SafetyLoop',
    title: 'Hazard logged · inspection booked',
    sub: 'The safety answer — crew briefed at the morning toolbox talk',
  },
  {
    tone: 'slate',
    icon: BadgeIcon,
    who: 'Exec',
    title: 'Stamped to the record, export-ready',
    sub: "The executive's answer — no change order yet",
  },
];

// Sequence steps: 0 = reset (everything fades out), 1 = field note lands,
// 2..5 = timeline events appear, then hold and loop.
const NOTE_STEP = 1;
const LAST_STEP = NOTE_STEP + recordEvents.length;
const STEP_DELAYS = {
  0: 550,
  1: 1700,
  [LAST_STEP]: 3600,
};

export default function HeroSection() {
  const [step, setStep] = useState(0);
  const [paused, setPaused] = useState(false);
  const reducedMotion = useRef(false);
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      reducedMotion.current = prefersReduced;
      if (prefersReduced) {
        setReduced(true);
        setStep(LAST_STEP);
      }
    }
  }, []);

  // Looping story: note lands, the record builds event by event, hold, restart.
  useEffect(() => {
    if (reduced || paused) return undefined;
    const delay = STEP_DELAYS[step] ?? 1150;
    const id = setTimeout(() => {
      setStep((s) => (s >= LAST_STEP ? 0 : s + 1));
    }, delay);
    return () => clearTimeout(id);
  }, [step, paused, reduced]);

  const eventsOn = Math.max(0, step - NOTE_STEP);
  const progress = eventsOn / recordEvents.length;

  return (
    <section className="plh-hero">
      <div className="plh-hero__bg" aria-hidden="true" />
      <div className="plh-hero__inner">
        <div className="plh-hero__copy">
          <p className="plh-eyebrow">The field-first construction platform</p>
          <h1>
            Everything your jobsite needs.
            <br />
            At <span className="plh-hero__accent">a fraction of&nbsp;the&nbsp;price.</span>
          </h1>
          <p className="plh-hero__subhead">
            Drawings, RFIs, daily logs, punch lists. Every workflow built for the field, every
            screen built for speed.
          </p>
          <p className="plh-hero__lede">
            ProjectLoop is the complete construction platform for Canadian builders. $39 per user
            per month, or CA$599/mo flat fee per project. A $5M renovation or a $200M tower, same
            monthly fee. No annual contracts. Fully integrated with SafetyLoop, TrafficLoop and
            ComplyLoop. Built in Ontario. Priced in CAD.
          </p>
          <ul className="plh-hero__checks">
            {checkRows.map((row) => (
              <li key={row}>
                <CheckIcon />
                <span>{row}</span>
              </li>
            ))}
          </ul>
          <div className="plh-hero__ctas">
            <Link to="/signup" className="pl-btn pl-btn--primary pl-btn--shine">
              Start 90-day free trial
            </Link>
            <Link to="/pricing" className="pl-btn pl-btn--ghost">
              See pricing
            </Link>
          </div>
          <p className="plh-hero__trust plh-mono">
            Built in Ontario · CAD pricing · Canadian data residency
          </p>
        </div>

        <div className="plh-hero__visual">
          <div
            className="plh-record"
            role="img"
            aria-label="A live ProjectLoop record: one field note from a foreman becoming an RFI, a schedule flag, a SafetyLoop inspection, and a stamped answer for the PM, superintendent, safety team, and executive"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
          >
            <div className="plh-record__bar">
              <span className="plh-record__mark" aria-hidden="true">
                <LoopIcon />
              </span>
              <div className="plh-record__title">
                <strong>Riverbend Tower — L3</strong>
                <span className="plh-mono">Project record · REC-2481</span>
              </div>
              <span className="plh-record__live plh-mono">
                <span className="plh-record__pulse" aria-hidden="true" />
                Live
              </span>
            </div>

            <div className={`plh-record__note${step >= NOTE_STEP ? ' is-on' : ''}`}>
              <div className="plh-record__author">
                <span className="plh-record__avatar" aria-hidden="true">MJ</span>
                <div>
                  <strong>Marcus Jarvis</strong>
                  <span>Foreman · Crew 4 · 4:47 PM</span>
                </div>
              </div>
              <p>
                &ldquo;Mechanical rough-in on L3 clashes with the beam at grid C-4. Photos attached
                — need direction before Friday&rsquo;s pour.&rdquo;
              </p>
              <div className="plh-record__attach" aria-hidden="true">
                <span>
                  <DrawingsIcon /> A-12.19
                </span>
                <span>
                  <CaptureIcon /> 2 photos
                </span>
                <span className="plh-record__attach--sync">Synced offline</span>
              </div>
            </div>

            <div className="plh-record__timeline">
              <span
                className="plh-record__timeline-fill"
                aria-hidden="true"
                style={{ transform: `scaleY(${progress})` }}
              />
              {recordEvents.map((event, idx) => {
                const Icon = event.icon;
                const isOn = eventsOn > idx;
                return (
                  <div
                    className={`plh-record__event plh-record__event--${event.tone}${isOn ? ' is-on' : ''}`}
                    key={event.who}
                  >
                    <span className="plh-record__dot" aria-hidden="true">
                      <Icon />
                    </span>
                    <div className="plh-record__event-body">
                      <strong>
                        {event.title}
                        {event.via && (
                          <span className="plh-record__via plh-mono">{event.via}</span>
                        )}
                      </strong>
                      <p>{event.sub}</p>
                    </div>
                    <span className="plh-record__who plh-mono">{event.who}</span>
                  </div>
                );
              })}
            </div>

            <div className={`plh-record__foot${step >= LAST_STEP ? ' is-on' : ''}`}>
              <strong>One capture. Four answers. One connected record.</strong>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
